/**
 * NomSodPage — Imai 1998 NOM-driven Sediment Oxygen Demand enhancement
 *
 * Natural Organic Matter (humic + fulvic acids) in lake water and porewater enhances SOD by
 * providing electron donors that fuel bacterial respiration at the sediment-water interface.
 * Imai et al. (1998) quantified this in eutrophic Japanese reservoirs:
 *     SOD_corrected = SOD_base × (1 + 0.12 × NOM_mg_L)   (capped at 3 × base)
 *
 * Reference: Imai, A., Fukushima, T. & Matsushige, K. (1998) "Effects of iron limitation and
 *            aquatic humic substances on the growth of Microcystis aeruginosa" Can. J. Fish.
 *            Aquat. Sci. 56: 1929-1937, National Institute for Environmental Studies, Tsukuba.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Droplets, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

export default function NomSodPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('nom_sod');
  const [nom, setNom] = useState(8);             // mg/L NOM
  const [sodBase, setSodBase] = useState(1.4);   // g O2 m⁻² d⁻¹
  const [tempC, setTempC] = useState(15);        // °C

  const R = useMemo(() => {
    // Imai 1998 calibration — NOM enhancement factor
    const enhancement = 1 + 0.12 * nom;
    const enhanceCapped = Math.min(enhancement, 3.0);
    // Arrhenius temperature correction (θ = 1.07 for SOD)
    const tempFactor = Math.pow(1.07, tempC - 20);
    const sodCorrected = sodBase * enhanceCapped * tempFactor;
    // Equivalent oxygen demand on hypolimnion (mg O2/L/d for 5 m hypolimnion)
    const o2DepletionRate = sodCorrected / 5;  // mg/L/d
    return { enhancement, enhanceCapped, tempFactor, sodCorrected, o2DepletionRate };
  }, [nom, sodBase, tempC]);

  const fmt = (n: number, d = 2) => isFinite(n) ? n.toFixed(d) : '—';

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Droplets} title="Module — NOM-driven SOD Enhancement"
        subtitle="Imai 1998: Natural Organic Matter accelerates sediment oxygen demand"
        actions={<div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
            <input type="checkbox" checked={enabled} onChange={() => toggle('nom_sod')} className="accent-emerald-500" /> Module {enabled ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={() => navigate('/modules')} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"><ArrowLeft className="w-4 h-4" /> Modules</button>
        </div>}
      />
      <ModuleConflictBanner moduleId="nom_sod" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">NOM enhancement factor</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">× {fmt(R.enhanceCapped, 2)}</div>
          <div className="text-xs text-slate-500 mt-1">{R.enhancement > 3 ? 'capped at 3.0' : 'within range'}</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Temperature factor</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">× {fmt(R.tempFactor, 2)}</div>
          <div className="text-xs text-slate-500 mt-1">θ = 1.07 (Arrhenius)</div>
        </div>
        <div className="rounded-xl border-2 border-red-700 bg-red-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-red-300">Corrected SOD</div>
          <div className="text-2xl font-bold text-red-200 mt-1">{fmt(R.sodCorrected, 2)}</div>
          <div className="text-xs text-slate-500 mt-1">g O₂ m⁻² d⁻¹</div>
        </div>
        <div className="rounded-xl border-2 border-amber-700 bg-amber-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-amber-300">Hypolimnion O₂ depletion</div>
          <div className="text-2xl font-bold text-amber-200 mt-1">{fmt(R.o2DepletionRate, 2)}</div>
          <div className="text-xs text-slate-500 mt-1">mg/L/d (5 m hypolimnion)</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
        <div>
          <label className="text-slate-400 block mb-1">NOM concentration: <strong className="text-slate-100">{nom} mg/L</strong></label>
          <input type="range" min={0} max={25} step={0.5} value={nom} onChange={e => setNom(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Base SOD: <strong className="text-slate-100">{sodBase.toFixed(2)} g/m²/d</strong></label>
          <input type="range" min={0.3} max={4} step={0.1} value={sodBase} onChange={e => setSodBase(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Hypolimnion T: <strong className="text-slate-100">{tempC} °C</strong></label>
          <input type="range" min={4} max={25} step={0.5} value={tempC} onChange={e => setTempC(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-rb-brand" /> Physical mechanism</div>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Humic and fulvic acids (NOM) dissolved in the water column diffuse into porewater near the sediment-water interface.</li>
          <li>Heterotrophic bacteria use NOM as electron donor, consuming O₂ at the interface.</li>
          <li>Iron-NOM complexes (Fe-humate) also undergo abiotic O₂ scavenging.</li>
          <li>Net effect: SOD enhancement scaling with the dissolved NOM concentration.</li>
        </ol>
        <p className="mt-2">When this module is enabled, the Sediment / Algal Dynamics pages multiply the baseline SOD by the
        NOM enhancement factor (capped × 3) and apply Arrhenius temperature correction. The result is fed into the hypolimnion
        oxygen balance, which determines whether anoxia develops and triggers downstream redox-P release.</p>
        <p className="italic text-slate-500 mt-2">Reference: Imai, Fukushima &amp; Matsushige (1998) Can. J. Fish. Aquat. Sci. 56: 1929-1937, NIES, Tsukuba.</p>
      </div>
    </div>
  );
}
